import { useState, useMemo, useRef, useCallback } from 'react';
import {
  SelectRowEvent,
  DataGridProps,
  CalculatedColumn,
  ISelectorEvent,
  IRange,
  ISelectRange,
  RangeMode,
} from '../types';
import { defaultSelectedRange } from '../utils/selectRange';
import { isCellEditable } from '../utils';
import { useLatestFunc } from './useLatestFunc';

interface SelectColumnEvent {
  columnIdx: number;
  isShiftClick: boolean;
  isCtrlClick: boolean;
}

export function useSelection<R, SR>(
  rows: DataGridProps<R>['initialRows'],
  columns: readonly CalculatedColumn<R, SR>[],
  isCellWithinRange: (range: IRange) => boolean,
  rowKeyGetter: (row: R) => string,
  onSelectChange: DataGridProps<R>['onSelectChange'],
) {
  const [selectedRange, setSelectedRange] = useState<ISelectRange>(defaultSelectedRange);
  const [selectedColumns, setSelectColumns] = useState<Set<string>>(new Set());
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const startIdxRef = useRef(0);
  const isSelectedRangeEditable = useMemo(() => {
    return (
      isCellWithinRange(selectedRange) &&
      isCellEditable<R, SR>({
        row: rows[selectedRange.rowIdx],
        column: columns[selectedRange.columnIdx],
      })
    );
  }, [selectedRange, isCellWithinRange, rows, columns]);

  const selectRow = useCallback(
    ({ rowIdx, isShiftClick, isCtrlClick }: SelectRowEvent) => {
      const row = rows[rowIdx];
      const rowKey = rowKeyGetter(row);
      const checked = selectedRows?.has(rowKey) ?? false;
      const nextSelectedRows = new Set(selectedRows);
      if (isCtrlClick) {
        if (!checked) {
          nextSelectedRows.add(rowKey);
        } else {
          nextSelectedRows.delete(rowKey);
        }
      } else if (isShiftClick) {
        const endIdx = rowIdx;
        const minIndex = Math.min(startIdxRef.current, endIdx);
        const maxIndex = Math.max(startIdxRef.current, endIdx);
        nextSelectedRows.clear();
        for (let i = minIndex; i <= maxIndex; i++) {
          nextSelectedRows.add(rowKeyGetter(rows[i]));
        }
      } else {
        nextSelectedRows.clear();
        if (!checked) {
          nextSelectedRows.add(rowKey);
          startIdxRef.current = rowIdx;
        }
      }
      setSelectColumns(new Set());
      setSelectedRows(nextSelectedRows);
      setSelectedRange(defaultSelectedRange);
      onSelectChange?.([...nextSelectedRows], []);
    },
    [rows, rowKeyGetter, selectedRows, startIdxRef, onSelectChange],
  );

  const selectColumn = useCallback(
    ({ columnIdx, isShiftClick, isCtrlClick }: SelectColumnEvent) => {
      const key = columns[columnIdx]?.key;
      if (!key) {
        return;
      }
      const checked = selectedColumns?.has(key) ?? false;
      const nextSelectedColumns = new Set(selectedColumns);
      if (isCtrlClick) {
        if (!checked) {
          nextSelectedColumns.add(key);
        } else {
          nextSelectedColumns.delete(key);
        }
      } else if (isShiftClick) {
        const endIdx = columnIdx;
        const minIndex = Math.min(startIdxRef.current, endIdx);
        const maxIndex = Math.max(startIdxRef.current, endIdx);
        nextSelectedColumns.clear();
        for (let i = minIndex; i <= maxIndex; i++) {
          if (columns[i]?.key) {
            nextSelectedColumns.add(columns[i].key);
          }
        }
      } else {
        nextSelectedColumns.clear();
        if (!checked) {
          nextSelectedColumns.add(key);
          startIdxRef.current = columnIdx;
        }
      }
      setSelectColumns(nextSelectedColumns);
      if (columnIdx !== 0) {
        setSelectedRows(new Set());
      }
      setSelectedRange(defaultSelectedRange);
      onSelectChange?.([], [...nextSelectedColumns]);
    },
    [columns, selectedColumns, startIdxRef, onSelectChange],
  );

  const selectCell = useCallback(
    (position: ISelectRange, isShiftClick = false, enableEditor = false) => {
      if (!isCellWithinRange(position)) {
        return;
      }
      if (enableEditor && isSelectedRangeEditable) {
        setSelectedRange({
          ...position,
          mode: RangeMode.EDIT,
          key: null,
        });
      } else {
        if (isShiftClick) {
          const { endColumnIdx, endRowIdx } = position;
          setSelectedRange({ ...selectedRange, endColumnIdx, endRowIdx, mode: RangeMode.SELECT });
        } else {
          setSelectedRange({ ...position, mode: RangeMode.SELECT });
        }
      }
      setSelectColumns(new Set());
      if (![-1, 0].includes(position.columnIdx)) {
        setSelectedRows(new Set());
      }
      if (position.rowIdx < 0) {
        onSelectChange?.([]);
      } else {
        const { rowIdx, endRowIdx, columnIdx, endColumnIdx } = position;
        onSelectChange?.(
          rows
            ?.slice(Math.min(rowIdx, endRowIdx), Math.max(rowIdx, endRowIdx) + 1)
            .map((row) => rowKeyGetter(row)) || [],
          columns
            ?.slice(Math.min(columnIdx, endColumnIdx), Math.max(columnIdx, endColumnIdx) + 1)
            .map((column) => column.key),
        );
      }
    },
    [isCellWithinRange, selectedRange, rows, isSelectedRangeEditable, onSelectChange],
  );

  const selectAllRows = useCallback(() => {
    if (!rows?.length) {
      return;
    }
    const rowIndexs = rows.map((row) => rowKeyGetter(row));
    const columnIndexs = columns.map((column) => column.key);
    const newSelectedRows = new Set<string>(rowIndexs);
    const newSelectedColumns = new Set<string>(columnIndexs);
    setSelectedRows(newSelectedRows);
    setSelectColumns(newSelectedColumns);
    setSelectedRange({
      ...defaultSelectedRange,
      columnIdx: 0,
      rowIdx: 0,
      endColumnIdx: columns.length - 1,
      endRowIdx: rows.length - 1,
    });
    onSelectChange?.([...newSelectedRows], [...newSelectedColumns]);
  }, [rows, columns]);

  const handleSelect = useLatestFunc(
    useCallback(
      (data: ISelectorEvent) => {
        const {
          rowIdx,
          columnIdx,
          isShiftClick = false,
          isCtrlClick = false,
          isUpdateIndex = true,
          enableEditor = false,
        } = data;
        if (rowIdx === -1 || columnIdx === -1) {
          if (rowIdx === -1 && columnIdx === -1) {
            selectAllRows();
          } else if (rowIdx === -1) {
            selectColumn({ columnIdx, isShiftClick, isCtrlClick });
          } else {
            selectRow({ rowIdx, isShiftClick, isCtrlClick });
          }
        } else {
          const _selectedRange = {
            ...selectedRange,
            endColumnIdx: columnIdx,
            endRowIdx: rowIdx,
          };
          if (isUpdateIndex) {
            _selectedRange.columnIdx = columnIdx;
            _selectedRange.rowIdx = rowIdx;
          }
          selectCell(_selectedRange, false, enableEditor);
        }
      },
      [selectAllRows, selectColumn, selectRow, selectedRange, selectCell],
    ),
  );

  return {
    selectedRange,
    selectedRows,
    selectedColumns,
    isSelectedRangeEditable,
    handleSelect,
  };
}
